import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { FixaInputField } from "@coworker/apprestructured/src/shared/wrappers/FixaInputField/FixaInputField";
import { FixaListViewItem } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListViewItem";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaModalFooter } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalFooter";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import { useTranslation } from "@coworker/locales";
import { useCallback, useMemo, useState } from "react";
import { FixaModalBody } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalBody";
import useSaveGuardedFunction from "@coworker/apprestructured/src/save-guard/useSaveGuardedFunction";
import InputWrapper from "../InputWrapper/InputWrapper";

type TextModalProps = {
  title: string;
  text: string;
  onConfirmClick: (newText: string) => void;
  disabled: boolean;
  maxLength: number;
};

function TextModal(props: TextModalProps) {
  const [showModal, setShowModal] = useState(false);
  const [newText, setNewText] = useState<string>(props.text);

  const { t } = useTranslation();

  const handleOpenModal = useCallback(() => {
    setNewText(props.text);
    setShowModal(true);
  }, [props.text]);

  const {
    saveGuardedFunction: handleCloseModal,
    enableSaveGuard,
    disableSaveGuard,
  } = useSaveGuardedFunction(
    useCallback(() => {
      setShowModal(false);
    }, [])
  );

  const textError = useMemo(() => {
    if (newText.length > props.maxLength) {
      return t("maximumCharactersLengthString", { count: props.maxLength });
    }
    return null;
  }, [newText, props.maxLength, t]);

  const handleConfirm = useCallback(() => {
    if (textError) return;
    props.onConfirmClick(newText);
    setShowModal(false);
    disableSaveGuard();
  }, [props, newText, disableSaveGuard, textError]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setNewText(e.target.value);
      enableSaveGuard();
    },
    [enableSaveGuard]
  );

  return (
    <>
      <FixaListViewItem
        title={props.title}
        inset
        onClick={handleOpenModal}
        emphasised={!!props.text}
        description={props.text}
        control="navigational"
        controlIcon={null}
        disabled={props.disabled}
      />
      <FixaModal
        visible={showModal}
        handleCloseBtn={handleCloseModal}
        data-testid="TextModal-FixaModal"
      >
        <FixaSheets
          header={
            <FixaModalHeader
              title={props.title}
              closeBtnClick={handleCloseModal}
            />
          }
          footer={
            <FixaModalFooter renderBorder>
              <FixaButton
                type="primary"
                fluid
                text={t("confirmString")}
                onClick={handleConfirm}
              />
            </FixaModalFooter>
          }
          fullSize
        >
          <FixaModalBody>
            <InputWrapper error={textError}>
              <FixaInputField
                type="text"
                id="text"
                value={newText}
                onChange={handleChange}
              />
            </InputWrapper>
          </FixaModalBody>
        </FixaSheets>
      </FixaModal>
    </>
  );
}

export default TextModal;
